import * as def from '@/definitions'
import { PathLayer } from './pathLayer'
import { ColorConstruct } from '@/utilityModels/colorConstruct'
import { Layer } from './layer';
import { Vector2 } from '@/utilityModels/vector2';

export class LineLayer extends Layer
{
    public get layerType() : string { return "line"; }

    // 線色
    private _strokeColor : ColorConstruct = new ColorConstruct();
    public get strokeColor()
    {
        return this._strokeColor;
    }
    public set strokeColor(strokeColor : ColorConstruct)
    {
        this._strokeColor = strokeColor;   
    }

    // 不透明度
    private _opacity : number = 100;
    public get opacity()
    {
        return this._opacity;
    }
    public set opacity(opacity : number)
    {
        this._opacity = opacity;
    }

    // 線幅
    private _strokeWidth : number = 1;
    public get strokeWidth()
    {
        return this._strokeWidth;
    }
    public set strokeWidth(strokeWidth : number)
    {
        this._strokeWidth = strokeWidth;
    }

    // 線種
    // 0 : 通常
    // 1 : 点線
    // 2 : 点線(広い)
    // 3 : 破線
    // 4 : 破線 + 点線
    private _strokeType : number = 0;
    public get strokeType()
    {
        return this._strokeType;
    }
    public set strokeType(strokeType : number)
    {
        this._strokeType = strokeType;
    }

    // 始点
    // 0 : 通常
    // 1 : 単線矢印
    // 2 : 塗りつぶし矢印
    // 3 : 矢じり
    // 4 : 丸
    // 5 : ひし形
    private _startType : number = 0;
    public get startType()
    {
        return this._startType;
    }
    public set startType(startType : number)
    {
        this._startType = startType;
    }

    // 始点幅
    private _startWidth : number = 4;
    public get startWidth()
    {
        return this._startWidth;
    }
    public set startWidth(startWidth : number)
    {
        this._startWidth = startWidth;
    }

    // 終点
    // 0 : 通常
    // 1 : 単線矢印
    // 2 : 塗りつぶし矢印
    // 3 : 矢じり
    // 4 : 丸
    // 5 : ひし形
    private _endType : number = 0;
    public get endType()
    {
        return this._endType;
    }
    public set endType(endType : number)
    {
        this._endType = endType;
    }

    // 終点幅
    private _endWidth : number = 4;
    public get endWidth()
    {
        return this._endWidth;
    }
    public set endWidth(endWidth : number)
    {
        this._endWidth = endWidth;
    }

    // 開始点
    private _startX : number = 0;
    public get startX() : number { return this._startX; }
    public set startX(startX : number) { this._startX = startX; }
    private _startY : number = 0;
    public get startY() : number { return this._startY; }
    public set startY(startY : number) { this._startY = startY; }

    // 終了点
    private _endX : number = 0;
    public get endX() : number { return this._endX; }
    public set endX(endX : number) { this._endX = endX; }
    private _endY : number = 0;
    public get endY() : number { return this._endY; }
    public set endY(endY : number) { this._endY = endY; }

    // 座標
    public get x() : number
    {
        return this.startX;
    }
    public set x(x : number)
    {
        var deltaX = x - this.startX;
        this.startX = x;
        // 終点も一緒に動かす。
        this.endX += deltaX;
    }
    public get y() : number
    {
        return this.startY;
    }
    public set y(y : number)
    {
        var deltaY = y - this.startY;
        this.startY = y;
        // 終点も一緒に動かす。
        this.endY += deltaY;
    }

    // 幅・高さ
    public get width() : number
    {
        return this.endX - this.startX;
    }
    public set width(width : number)
    {
        this.endX = this.startX + width;
    }
    public get height() : number
    {
        return this.endY - this.startY;
    }
    public set height(height : number)
    {
        this.endY = this.startY + height;
    }

    // 回転角
    public get rotate() : number
    {
        // 回転角を求める
        var radian = Math.atan2(this.endY - this.startY, this.endX - this.startX);
        var degrees = radian * 180 / Math.PI;
        while(degrees > 180) { degrees -= 360; }
        while(degrees < -180) { degrees += 360; } 
        return degrees;
    }
    public set rotate(rotate : number)
    {
        // 現在の回転角
        var currentRad = Math.atan2(this.endY - this.startY, this.endX - this.startX);
        // 中心点
        var cx = (this.startX + this.endX) / 2;
        var cy = (this.startY + this.endY) / 2;
        // 中心点を中心に回転角の逆に始点・終点を回転させて基準点に戻す
        var startOrigin = (new Vector2(this.startX, this.startY)).rotate(-currentRad, cx, cy);
        var endOrigin = (new Vector2(this.endX, this.endY)).rotate(-currentRad, cx, cy);
        // この始点・終点を指定の回転角だけ回転させて、新しい始点・終点を求める
        var startNew = startOrigin.rotateDegrees(rotate, cx, cy);
        var endNew = endOrigin.rotateDegrees(rotate, cx, cy);
        this.startX = startNew.x;
        this.startY = startNew.y;
        this.endX = endNew.x;
        this.endY = endNew.y;
    }

    // 頂点は外接矩形でとる
    public getVertexes() : Vector2[]
    {
        var minX = Math.min(this.startX, this.endX);
        var maxX = Math.max(this.startX, this.endX);
        var minY = Math.min(this.startY, this.endY);
        var maxY = Math.max(this.startY, this.endY);

        var vertexes = new Array();
        vertexes.push(new Vector2(minX, minY));
        vertexes.push(new Vector2(minX, maxY));
        vertexes.push(new Vector2(maxX, maxY));
        vertexes.push(new Vector2(maxX, minY));
        return vertexes;
    }

    // 作成
    public static create(startX : number, startY : number, endX : number, endY : number)
    {
        var layer = new LineLayer();
        layer.startX = startX;
        layer.startY = startY;
        layer.endX = endX;
        layer.endY = endY;

        return layer;
    }

    // このレイヤーに触られている
    public isTouched : boolean = false;
    public isPointContained(x : number, y : number) : boolean
    {
        return this.isTouched;
    }

    // 複製
    public clone()
    {
        var cloned = new LineLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : LineLayer)
    {
        layer.opacity = this._opacity;
        layer.strokeColor = this._strokeColor.clone();
        layer.strokeWidth = this._strokeWidth;
        layer.strokeType = this._strokeType;
        layer.startType = this._startType;
        layer.startWidth = this._startWidth;
        layer.endType = this._endType;
        layer.endWidth = this._endWidth;
        layer.startX = this._startX;
        layer.startY = this._startY;
        layer.endX = this._endX;
        layer.endY = this._endY;
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.opacity = this._opacity;
        jsonObject.strokeColor = this._strokeColor;
        jsonObject.strokeWidth = this._strokeWidth;
        jsonObject.strokeType = this._strokeType;
        jsonObject.startType = this._startType;
        jsonObject.startWidth = this._startWidth;
        jsonObject.endType = this._endType;
        jsonObject.endWidth = this._endWidth;
        jsonObject.startX = this._startX;
        jsonObject.startY = this._startY;
        jsonObject.endX = this._endX;
        jsonObject.endY = this._endY;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : LineLayer
    {
        var layer = new LineLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.opacity != null) { this._opacity = jsonData.opacity; }
        if(jsonData.strokeColor != null) { this._strokeColor = ColorConstruct.createFromJsonData(jsonData.strokeColor); }
        if(jsonData.strokeWidth != null) { this._strokeWidth = jsonData.strokeWidth; }
        if(jsonData.strokeType != null) { this._strokeType = jsonData.strokeType; }
        if(jsonData.startType != null) { this._startType = jsonData.startType; }
        if(jsonData.startWidth != null) { this._startWidth = jsonData.startWidth; }
        if(jsonData.endType != null) { this._endType = jsonData.endType; }
        if(jsonData.endWidth != null) { this._endWidth = jsonData.endWidth; }
        if(jsonData.startX != null) { this._startX = jsonData.startX; }
        if(jsonData.startY != null) { this._startY = jsonData.startY; }
        if(jsonData.endX != null) { this._endX = jsonData.endX; }
        if(jsonData.endY != null) { this._endY = jsonData.endY; } 

        super.loadParameterFromJson(jsonData);
    }
}